<template>
    <VJoyPanel class="team-panel" :class="{isDesktopScreen}">
        <template #panel-title>{{ t('projectclient.projectview.sidepanel.team.info.title') }}</template>
        <template #panel-body>
            <VJoyWrapper row-gap="4" direction="column" align="flex-start">
                <VJoyWrapper row-gap="0" no-margin direction="column" align="flex-start" class="full-width">
                    <p class="team-panel__role">{{ t('projectclient.projectview.sidepanel.team.info.freelancer') }}</p>
                    <VJoyWrapper justify="space-between" wrap="nowrap" no-margin class="full-width">
                        <div class="team-panel__info-container / flex">
                            <VJoyAvatar
                                class="team-panel__info-container__avatar"
                                size="small"
                                :photo-url="projectInformation.freelancer.pictureUrl"
                                :full-name="projectInformation.freelancer.fullName"
                            />
                            <div class="team-panel__info-container__about / flex">
                                <p class="team-panel__info-container__name ellipsis" :title="projectInformation.freelancer.fullName">
                                    {{ projectInformation.freelancer.fullName }}
                                </p>
                                <p :title="projectInformation.freelancer.headline" class="team-panel__info-container__job-title ellipsis">
                                    {{ projectInformation.freelancer.headline }}
                                </p>
                            </div>
                        </div>
                        <div class="team-panel__actions / flex" :class="{isDesktopScreen}">
                            <VJoyIconButton size="small" variant="ghost" icon="user-lighter" @click="displayFreelancerProfileDrawer" />
                            <VJoyIconButton
                                v-if="canSeeMessagesTab"
                                size="small"
                                variant="ghost"
                                icon="message"
                                @click="displayFreelancerMessengerDrawer"
                            />
                        </div>
                    </VJoyWrapper>
                </VJoyWrapper>
                <VJoyWrapper row-gap="0" no-margin direction="column" align="flex-start" class="full-width">
                    <p class="team-panel__role">{{ t('projectclient.projectview.sidepanel.team.info.client') }}</p>
                    <VJoyWrapper justify="space-between" wrap="nowrap" no-margin class="full-width">
                        <div class="team-panel__info-container / flex">
                            <VJoyAvatar
                                class="team-panel__info-container__avatar"
                                size="small"
                                :photo-url="projectInformation.client.pictureUrl"
                                :full-name="projectInformation.client.fullName"
                            />
                            <div :title="projectInformation.client.fullName" class="team-panel__info-container__about / flex">
                                <p class="team-panel__info-container__name ellipsis" data-testid="project-owner-fullname">
                                    {{ projectInformation.client.fullName }}
                                </p>
                            </div>
                        </div>
                        <ChangeProjectOwnerOverlay :current-project-owner="projectInformation.client.fullName" />
                    </VJoyWrapper>
                </VJoyWrapper>
                <VJoyWrapper v-if="showCustomerSuccessManager" row-gap="0" no-margin direction="column" align="flex-start" class="full-width">
                    <p class="team-panel__role">{{ $t('projectclient.projectview.sidepanel.team.info.customer-success-manager') }}</p>
                    <VJoyWrapper justify="space-between" wrap="nowrap" no-margin class="full-width">
                        <div class="team-panel__info-container / flex">
                            <VJoyAvatar
                                class="team-panel__info-container__avatar"
                                size="small"
                                :photo-url="customerSuccessManagerPhotoUrl"
                                :full-name="projectInformation.customerSuccessManager?.fullName"
                            />
                            <div :title="projectInformation.customerSuccessManager?.fullName" class="team-panel__info-container__about / flex">
                                <p class="team-panel__info-container__name ellipsis" data-testid="customer-success-manager-fullname">
                                    {{ projectInformation.customerSuccessManager?.fullName }}
                                </p>
                                <p class="team-panel__info-container__job-title ellipsis">
                                    {{ $t('projectclient.projectview.sidepanel.team.info.customer-success-manager.display-title') }}
                                </p>
                            </div>
                        </div>
                        <div class="team-panel__actions / flex" :class="{isDesktopScreen}">
                            <VJoyIconButton
                                size="small"
                                variant="ghost"
                                icon="envelope"
                                :label="t('projectclient.projectview.sidepanel.team.info.customer-success-manager.email.copy')"
                                data-testid="copy-customer-success-manager-email"
                                @click="copyEmailToClipboard"
                            />
                        </div>
                    </VJoyWrapper>
                </VJoyWrapper>
            </VJoyWrapper>
        </template>
    </VJoyPanel>
    <FreelancerDrawer
        :freelancer-info="computedFreelancerInfo"
        :conversation-id="projectInformation.contract.conversationId"
        :is-client-owner="isClientProjectOwner"
        :client-identity-id="projectInformation.client.identityId"
    />
</template>

<script setup lang="ts">
    import {useScreenSizeStore, useProjectInformationStore, useUserStore} from '@common/stores';
    import {FreelancerDrawer} from '@common/ui';
    import {useClipboard} from '@vueuse/core';
    import {storeToRefs} from 'pinia';
    import {computed, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {pushVJoySnackbar, VJoyAvatar, VJoyIconButton, VJoyPanel, VJoyWrapper} from '@maltjoy/core-vue';
    import {eventBus} from '@/common/events';
    import {FreelancerDrawerTab} from '@/features/project-view/types';
    import {ChangeProjectOwnerOverlay} from '@/features/project-view/ui/';

    const {t} = useTranslation();
    const {copy} = useClipboard({legacy: true});

    const screenSizeStore = useScreenSizeStore();
    const {isDesktopScreen} = storeToRefs(screenSizeStore);

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, isClientProjectOwner} = storeToRefs(projectInformationStore);
    const isCustomerSuccessManagerEnabled = useFeatureFlag('PROJ-3035-display-customer-success-manager');
    const showCustomerSuccessManager = computed(
        () => isCustomerSuccessManagerEnabled.value && Boolean(projectInformation.value.customerSuccessManager),
    );
    const customerSuccessManagerPhotoUrl = computed(() => {
        const client = projectInformation.value.client;
        const customerSuccessManager = projectInformation.value.customerSuccessManager;
        if (!client || !customerSuccessManager || customerSuccessManager.fullName !== client.fullName) {
            return undefined;
        }
        return client.pictureUrl;
    });

    const computedFreelancerInfo = computed(() => {
        const freelancer = projectInformation.value.freelancer;
        return {
            profileId: freelancer.profileId,
            fullName: freelancer.fullName,
            headline: freelancer.headline,
            photoUrl: freelancer.pictureUrl,
        };
    });

    const userStore = useUserStore();

    function displayFreelancerProfileDrawer() {
        eventBus.emit('showFreelancerDrawer', {tab: FreelancerDrawerTab.Profile});
    }

    function displayFreelancerMessengerDrawer() {
        eventBus.emit('showFreelancerDrawer', {tab: FreelancerDrawerTab.Messages});
    }

    function copyEmailToClipboard() {
        const email = projectInformation.value.customerSuccessManager?.email;
        if (!email) {
            return;
        }

        copy(email);
        pushVJoySnackbar({
            props: {
                header: t('projectclient.projectview.sidepanel.team.info.customer-success-manager.email.copied.header'),
                message: t('projectclient.projectview.sidepanel.team.info.customer-success-manager.email.copied.message', [email]),
                level: 'success',
            },
        });
    }

    const canSeeMessagesTab = computed(() => {
        return isClientProjectOwner.value || userStore.isPlatformAdmin;
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .full-width {
        width: 100%;
    }

    .team-panel {
        width: 100%;

        &__role {
            @include text.getFontBySize(primary-300);

            color: var(--joy-color-neutral-40);
            margin-bottom: var(--joy-core-spacing-1);
        }

        &__info-container {
            align-items: center;
            flex-wrap: nowrap;

            &__about {
                @include text.getFontBySize(primary-300);

                flex-direction: column;
                margin-left: var(--joy-core-spacing-2);
            }

            &__name {
                @include text.getFontBySize(primary-350);

                margin-bottom: 0;
            }

            &__job-title {
                @include text.getFontBySize(primary-300);
            }

            &__avatar {
                flex: 0 0 auto;
            }
        }

        &__actions {
            flex-flow: row nowrap;
            align-items: center;
            justify-content: flex-end;
            gap: var(--joy-core-spacing-2);
            flex: 1 0 auto;
        }
    }
</style>
